export type SegmentedTab<T extends string> = {
  id: T;
  label: string;
  /** Optional count badge shown after the label. */
  count?: number;
  /** Optional status-dot colour shown before the label. */
  dot?: string;
};

type SegmentedTabsProps<T extends string> = {
  tabs: SegmentedTab<T>[];
  value: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
  /** Extra classes on the container (e.g. width/flex behaviour). */
  className?: string;
};

/**
 * Compact segmented control (pill tabs) with optional status dots + counts.
 * The selected pill sits on a raised surface; the rest are quiet. Reused across
 * list pages for status filters (coupons, orders, …) — keep the styling here so
 * every filter row looks identical.
 */
export function SegmentedTabs<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  className = "",
}: SegmentedTabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={[
        "no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-[11px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] p-[3px]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {tabs.map((tab) => {
        const active = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={[
              "inline-flex shrink-0 items-center gap-1.5 rounded-[8px] px-2.5 py-[5px] text-[12.5px] transition-colors",
              active
                ? "bg-[color:var(--surface)] font-semibold text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                : "font-medium text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]",
            ].join(" ")}
          >
            {tab.dot ? (
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: tab.dot }}
              />
            ) : null}
            {tab.label}
            {tab.count !== undefined ? (
              <span
                className={[
                  "text-[10.5px] font-bold",
                  "text-[color:var(--text-muted)]",
                ].join(" ")}
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
